import { useState, useRef, useEffect, useMemo } from "react";
import "./SearchableSelect.css";

// A compact, stylish single-select dropdown with a live search box and a scrollable
// result list (handles long option lists far better than a giant native <select>).
// options: array of strings OR { value, label }. Controlled via value/onChange.
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "-- Select --",
  searchPlaceholder = "Search…",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const opts = useMemo(
    () => options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [opts, query]);

  const selectedLabel = useMemo(() => {
    const m = opts.find((o) => o.value === value);
    return m ? m.label : "";
  }, [opts, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    return () => { document.removeEventListener("mousedown", onDoc); clearTimeout(t); };
  }, [open]);

  const pick = (v) => { onChange(v); setOpen(false); setQuery(""); };

  return (
    <div className={`ss-root ${disabled ? "ss-disabled" : ""}`} ref={rootRef}>
      <button
        type="button"
        className={`ss-control ${open ? "ss-open" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className={`ss-value ${selectedLabel ? "" : "ss-placeholder"}`}>{selectedLabel || placeholder}</span>
        <span className="ss-icons">
          {value ? (
            <span className="ss-clear" role="button" title="Clear" onClick={(e) => { e.stopPropagation(); pick(""); }}>×</span>
          ) : null}
          <span className="ss-caret">▾</span>
        </span>
      </button>

      {open && (
        <div className="ss-menu">
          <div className="ss-search-wrap">
            <input
              ref={inputRef}
              className="ss-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key === "Enter" && filtered[0]) { e.preventDefault(); pick(filtered[0].value); }
              }}
            />
          </div>
          <div className="ss-list">
            {filtered.length === 0 ? (
              <div className="ss-empty">No matches</div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.value}
                  className={`ss-option ${o.value === value ? "ss-selected" : ""}`}
                  onClick={() => pick(o.value)}
                  title={o.label}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
          <div className="ss-footer">{filtered.length} of {opts.length}</div>
        </div>
      )}
    </div>
  );
}
